import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Crown, ShieldCheck, Search, ArrowLeft, MapPin, Building2, BarChart3, Sparkles, Mail, Phone, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_BG = {
  platinum: 'bg-gradient-to-br from-[#D4AF37] to-[#C8A028] text-[#0B192C]',
  gold: 'bg-[#D4AF37] text-[#0B192C]',
  silver: 'bg-slate-300 text-[#0B192C]',
  building: 'bg-white border border-slate-300 text-slate-600',
};

const TIER_ICON = {
  platinum: Crown,
  gold: ShieldCheck,
  silver: ShieldCheck,
  building: Sparkles,
};

const tierEs = { platinum: 'Platino', gold: 'Oro', silver: 'Plata', building: 'Construyendo Confianza' };

export default function TopAgentsPage() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');

  const tt = (en, es) => (language === 'es' ? es : en);

  useEffect(() => {
    setLoading(true);
    const params = cityFilter ? `?city=${encodeURIComponent(cityFilter)}&limit=200` : '?limit=200';
    axios.get(`${API}/admin/top-agents${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(() => toast.error(tt('Failed to load leaderboard', 'Error al cargar el ranking')))
      .finally(() => setLoading(false));
  }, [token, cityFilter]);

  const filtered = useMemo(() => {
    if (!data?.agents) return [];
    let list = data.agents;
    if (tierFilter !== 'all') {
      list = list.filter(a => a.trust_tier === tierFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(a =>
        a.name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        (a.city_suburb || '').toLowerCase().includes(q) ||
        (a.agency?.name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [data, search, tierFilter]);

  const stats = useMemo(() => {
    const list = data?.agents || [];
    return {
      total: list.length,
      platinum: list.filter(a => a.trust_tier === 'platinum').length,
      gold: list.filter(a => a.trust_tier === 'gold').length,
      silver: list.filter(a => a.trust_tier === 'silver').length,
      building: list.filter(a => a.trust_tier === 'building').length,
      verified: list.filter(a => a.verified).length,
      avg: list.length ? Math.round(list.reduce((s, a) => s + a.trust_score, 0) / list.length) : 0,
    };
  }, [data]);

  const cities = useMemo(() => {
    const set = new Set();
    (data?.agents || []).forEach(a => a.city_suburb && set.add(a.city_suburb));
    return Array.from(set).sort();
  }, [data]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="top-agents-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#D4AF37] mb-4" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" />
        {tt('Back to Admin', 'Volver al Admin')}
      </Link>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B192C] to-[#1B2A4E] text-white p-6 md:p-8 mb-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/20 border-2 border-[#D4AF37] flex items-center justify-center">
            <Trophy className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <div className="flex-1">
            <p className="uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-medium mb-1">
              {tt('Admin · Trust Leaderboard', 'Admin · Ranking de Confianza')}
            </p>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">
              {tt('Top-Rated Agents', 'Agentes Mejor Valorados')}
            </h1>
            <p className="text-sm text-slate-300 mt-2 max-w-2xl">
              {tt(
                'All agents on WakaCasa, ranked live by Trust Score. Use this internally to prioritise verification outreach, identify top performers, and spot agents who need profile help.',
                'Todos los agentes en WakaCasa, ordenados en vivo por Puntaje de Confianza. Úsalo internamente para priorizar la verificación, identificar los mejores agentes y detectar quienes necesitan ayuda con su perfil.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="leaderboard-stats">
        {[
          { k: 'total', label: tt('Total Agents', 'Total Agentes'), value: stats.total, icon: Building2, color: 'text-[#0B192C] bg-slate-100' },
          { k: 'platinum', label: tt('Platinum', 'Platino'), value: stats.platinum, icon: Crown, color: 'text-[#0B192C] bg-[#D4AF37]/30' },
          { k: 'gold', label: tt('Gold', 'Oro'), value: stats.gold, icon: ShieldCheck, color: 'text-[#0B192C] bg-[#D4AF37]/15' },
          { k: 'verified', label: tt('Verified', 'Verificados'), value: stats.verified, icon: ShieldCheck, color: 'text-green-700 bg-green-50' },
          { k: 'avg', label: tt('Avg Score', 'Puntaje Promedio'), value: stats.avg, icon: BarChart3, color: 'text-blue-700 bg-blue-50' },
        ].map(s => (
          <Card key={s.k} data-testid={`stat-${s.k}`}>
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}><s.icon className="w-4 h-4" /></div>
              <div><div className="font-heading text-lg font-bold">{s.value}</div><div className="text-[11px] text-slate-500 leading-tight">{s.label}</div></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-border p-3 mb-5 flex flex-col md:flex-row gap-3 items-stretch md:items-center" data-testid="leaderboard-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            placeholder={tt('Search by name, email, city or agency…', 'Buscar por nombre, correo, ciudad o agencia…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="leaderboard-search"
          />
        </div>
        <div className="flex gap-2">
          {[
            { v: 'all', label: tt('All', 'Todos') },
            { v: 'platinum', label: tt('Platinum', 'Platino') },
            { v: 'gold', label: 'Gold' },
            { v: 'silver', label: tt('Silver', 'Plata') },
            { v: 'building', label: tt('Building', 'Construyendo') },
          ].map(t => (
            <Button
              key={t.v}
              variant={tierFilter === t.v ? 'default' : 'outline'}
              size="sm"
              className={tierFilter === t.v ? 'bg-[#0B192C] hover:bg-[#1B2A4E] text-white h-9' : 'h-9'}
              onClick={() => setTierFilter(t.v)}
              data-testid={`tier-filter-${t.v}`}
            >
              {t.label}
            </Button>
          ))}
        </div>
        {cities.length > 0 && (
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="city-filter"
          >
            <option value="">{tt('All cities', 'Todas las ciudades')}</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {/* Leaderboard */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-xl text-slate-500" data-testid="leaderboard-empty">
          <Filter className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          {tt('No agents match these filters.', 'Ningún agente coincide con estos filtros.')}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid="leaderboard-table">
          {/* Header row */}
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50 border-b border-border">
            <div className="col-span-1">{tt('Rank', 'Rank')}</div>
            <div className="col-span-4">{tt('Agent', 'Agente')}</div>
            <div className="col-span-2">{tt('Trust Score', 'Puntaje')}</div>
            <div className="col-span-2 text-center">{tt('Listings', 'Propiedades')}</div>
            <div className="col-span-1 text-center">{tt('Sales', 'Ventas')}</div>
            <div className="col-span-2 text-right">{tt('Action', 'Acción')}</div>
          </div>
          {filtered.map((a, i) => {
            const TIcon = TIER_ICON[a.trust_tier] || Sparkles;
            const tierLabel = language === 'es' ? (tierEs[a.trust_tier] || a.trust_tier_label) : a.trust_tier_label;
            const initial = (a.name || '?').charAt(0).toUpperCase();
            const rank = i + 1;
            return (
              <div
                key={a.id}
                className={`grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-3 items-center border-b last:border-b-0 border-slate-100 hover:bg-slate-50 transition-colors ${rank <= 3 ? 'bg-gradient-to-r from-[#D4AF37]/5 to-transparent' : ''}`}
                data-testid={`leaderboard-row-${a.id}`}
              >
                {/* Rank */}
                <div className="md:col-span-1 flex items-center gap-1.5">
                  <span className={`font-heading text-lg font-bold ${rank === 1 ? 'text-[#D4AF37]' : rank === 2 ? 'text-slate-500' : rank === 3 ? 'text-amber-700' : 'text-slate-400'}`} data-testid={`rank-${a.id}`}>
                    #{rank}
                  </span>
                  {rank === 1 && <Crown className="w-4 h-4 text-[#D4AF37]" />}
                </div>

                {/* Agent identity */}
                <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                  <Avatar className="w-10 h-10 flex-shrink-0">
                    {a.photo ? <AvatarImage src={a.photo.startsWith('http') ? a.photo : `${process.env.REACT_APP_BACKEND_URL}${a.photo}`} alt={a.name} /> : null}
                    <AvatarFallback className="bg-[#0B192C] text-[#D4AF37] text-sm font-semibold">{initial}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-sm truncate">{a.name || '—'}</span>
                      {a.verified && (
                        <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px] gap-0.5 h-5 hover:bg-green-50">
                          <ShieldCheck className="w-2.5 h-2.5" />{tt('Verified', 'Verificado')}
                        </Badge>
                      )}
                      {a.agent_tier === 'pro' && (
                        <Badge className="bg-[#D4AF37] text-[#0B192C] text-[10px] gap-0.5 h-5 hover:bg-[#C8A028]">
                          <Crown className="w-2.5 h-2.5" />PRO
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                      <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{a.email}</span>
                      {a.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</span>}
                    </div>
                    {(a.city_suburb || a.agency?.name) && (
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                        {a.city_suburb && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{a.city_suburb}</span>}
                        {a.agency?.name && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{a.agency.name}</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Trust Score pill */}
                <div className="md:col-span-2">
                  <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${TIER_BG[a.trust_tier]}`} data-testid={`trust-pill-${a.id}`}>
                    <TIcon className="w-3 h-3" />
                    <span>{a.trust_score}</span>
                    <span className="opacity-70 font-normal">/100</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">{tierLabel}</div>
                </div>

                {/* Listings */}
                <div className="md:col-span-2 text-center">
                  <div className="font-heading text-base font-bold text-[#0B192C]" data-testid={`listings-${a.id}`}>{a.active_listings}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">{tt('active', 'activas')}</div>
                </div>

                {/* Sales */}
                <div className="md:col-span-1 text-center">
                  <div className="font-heading text-base font-bold text-[#D4AF37]">{a.completed_transactions}</div>
                </div>

                {/* Action */}
                <div className="md:col-span-2 flex justify-end">
                  <Link to={`/agent/${a.id}`} target="_blank" rel="noopener">
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid={`view-agent-${a.id}`}>
                      {tt('View profile', 'Ver perfil')}
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-slate-400 text-center mt-4">
        {tt(
          `Showing ${filtered.length} of ${data?.total || 0} agents · Live computation, sorted by Trust Score → Sales → Active Listings`,
          `Mostrando ${filtered.length} de ${data?.total || 0} agentes · Cálculo en vivo, ordenado por Puntaje → Ventas → Propiedades Activas`
        )}
      </p>
    </div>
  );
}
